/**
 * Détail tarifaire commande pour l'admin commerçant.
 */
import { parseLigneCommandeDetails } from './commandeLigneCommentaire';

export function getLignePricingDisplay(item = {}) {
  const produit = item.produit || {};
  const quantite = Math.max(1, Number(item.quantite) || 1);
  const lineTotal =
    Number(item.prixTotal ?? item.prixFinal) ||
    (Number(item.prixUnitaire) || 0) * quantite;

  const parsed = parseLigneCommandeDetails(item.commentaire, item.options);
  const isPlanche =
    produit.productType === 'PAR_PERSONNE' ||
    parsed?.kind === 'planche_perso' ||
    parsed?.kind === 'planche_variee';

  const nbPersons = parsed?.nbPersons || null;

  if (isPlanche && nbPersons) {
    let prixParPersonne = Number(produit.prixParPersonne ?? 0);
    if (!prixParPersonne && nbPersons > 0) {
      prixParPersonne = lineTotal / (nbPersons * quantite);
    }

    const unitLabel = `${prixParPersonne.toFixed(2)}€/pers`;
    const multLabel =
      quantite > 1
        ? `${unitLabel} × ${nbPersons} pers. × ${quantite} pl.`
        : `${unitLabel} × ${nbPersons} pers.`;

    return {
      isPlanche: true,
      lineTotal,
      label: multLabel,
    };
  }

  const prixUnitaire =
    quantite > 0 && lineTotal ? lineTotal / quantite : Number(item.prixUnitaire) || 0;

  return {
    isPlanche: false,
    lineTotal,
    label: `${prixUnitaire.toFixed(2)}€ × ${quantite}`,
  };
}

function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/** M1 — libellé avantage + points (miroir backend). */
export function formatPromotionPersonnelleLabel(meta) {
  const titre = meta?.titre != null ? String(meta.titre).trim() : '';
  const type = meta?.typeReduction ? String(meta.typeReduction) : '';
  const valeur = Number(meta?.reduction);
  let suffix = '';
  if (type === 'POURCENTAGE' && Number.isFinite(valeur) && valeur > 0) {
    suffix = ` -${valeur} %`;
  }
  return `${titre || 'Promotion personnelle'}${suffix}`;
}

function resolvePromotionPersonnelleMeta(commande) {
  if (commande?.promotionPersonnelle && typeof commande.promotionPersonnelle === 'object') {
    return commande.promotionPersonnelle;
  }
  const linked = commande?.utilisationsPromotionsClients?.[0]?.promotionClient;
  if (linked) {
    return {
      titre: linked.titre,
      typeReduction: linked.typeReduction,
      reduction: linked.reduction,
    };
  }
  return null;
}

export function formatAvantageFideliteLabel(points) {
  const n = Number(points);
  if (Number.isFinite(n) && n > 0) {
    return `Avantage fidélité — ${Math.floor(n)} points utilisés`;
  }
  return 'Avantage fidélité';
}

/** M1 — lignes réduction (miroir backend/services/reductionVentilation.js). */
export function buildReductionDisplayLines(commande) {
  const total = roundMoney(Math.max(0, Number(commande?.reduction) || 0));
  const permanente = roundMoney(Math.max(0, Number(commande?.reductionPromoPermanente) || 0));
  const code = roundMoney(Math.max(0, Number(commande?.reductionCodePromo) || 0));
  const avantage = roundMoney(Math.max(0, Number(commande?.reductionAvantage) || 0));
  const partsSum = roundMoney(permanente + code + avantage);

  const parts = [];
  if (permanente > 0) {
    parts.push({
      key: 'permanente',
      label: formatPromotionPersonnelleLabel(resolvePromotionPersonnelleMeta(commande)),
      amount: permanente,
    });
  }
  if (code > 0) {
    const codeStr = commande?.codePromo ? String(commande.codePromo) : null;
    parts.push({
      key: 'code',
      label: codeStr ? `Code promo « ${codeStr} »` : 'Code promo',
      amount: code,
      codePromo: codeStr,
    });
  }
  if (avantage > 0) {
    const points =
      commande?.avantagePointsUtilises != null
        ? Number(commande.avantagePointsUtilises)
        : null;
    parts.push({
      key: 'avantage',
      label: formatAvantageFideliteLabel(points),
      amount: avantage,
      points: Number.isFinite(points) ? points : null,
    });
  }

  const isLegacyAggregate = total > 0 && partsSum === 0;
  if (isLegacyAggregate) {
    const codeStr = commande?.codePromo ? String(commande.codePromo) : null;
    parts.push({
      key: 'legacy',
      label: codeStr ? `Réduction (${codeStr})` : 'Réduction',
      amount: total,
      codePromo: codeStr,
    });
  }

  return { parts, total, isLegacyAggregate };
}

export function getCommandePricingBreakdown(commande) {
  const frais = Number(commande?.fraisLivraison) || 0;
  const reduction = Number(commande?.reduction) || 0;
  const total = Number(commande?.total) || 0;

  const linesSum = (commande?.produits || []).reduce((sum, item) => {
    const line =
      Number(item.prixTotal) ||
      (Number(item.prixUnitaire) || 0) * (Number(item.quantite) || 1);
    return sum + line;
  }, 0);

  const sousTotalBrut =
    linesSum > 0 ? linesSum : Math.max(0, total - frais + reduction);

  const { parts: reductionLines } = buildReductionDisplayLines(commande);
  const reductionLabel =
    reductionLines.length === 1
      ? reductionLines[0].label
      : reductionLines.length > 1
        ? 'Réductions'
        : null;

  return {
    sousTotalBrut,
    reduction,
    frais,
    total,
    reductionLabel,
    reductionLines,
    hasReduction: reduction > 0,
  };
}
